import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ConditionCard, ConditionOption } from "@components/elements/Condition";
import { MedicationSelector } from "@components/elements/MedicationSelector";
import OverlayPanel from "@components/OverlayPanel";
import useUpsellConfig from "@hooks/cart/useUpsellConfig";
import useAvailableMedications from "@hooks/case/useAvailableMedications";
import useFetchMedications from "@hooks/case/useFetchMedications";
import { useQuestionValidation } from "@hooks/questionnaire/useQuestionValidation";
import { updateConditionMedication } from "@store/cases/casesSlice";
import { CaseUpdateInput } from "@types-runway/case";
import {
    Condition as ICondition, conditionTypeIndex, ConditionTypes, Medication
} from "@types-runway/condition";
import { NoticeMessage, WarningMessage } from "@types-runway/questionnaire";
import { RootState } from "@types-runway/state";
import { isConditionCartPreselected } from "@utils/cart/cartPreselection";
import { resolveDisplayedMedicationName } from "@utils/cart/upsellConfigLogic";
import { calculateMalariaPricing, resolveMedicationQuantity } from "@utils/medication/malariaPricing";

import ConditionBody from "./components/ConditionBody";
import ConditionInfoContent from "./components/ConditionInfoContent";

export type ConditionVariant =
  | "dashboard"
  | "consultation"
  | "upsell"
  | "region-based"
  | "comfort-care"
  | "feed";

type Props = {
  condition: ICondition;
  removeCondition?: (condition: string) => void;
  addCondition?: (conditionName: string, medication?: Medication) => void;
  variant?: ConditionVariant;
  cartItemPresentationStyle?: "card" | "selector";
  addonConditions?: ICondition[];
  discount?: number;
  warningMessage?: WarningMessage;
};

const Condition = ({
  condition,
  removeCondition,
  addCondition,
  variant,
  addonConditions = [],
  discount,
  cartItemPresentationStyle = "card",
  warningMessage,
}: Props) => {
  const isCartPreselected = isConditionCartPreselected(condition.product.name);
  const dispatch = useDispatch();
  const [overlayPanelIsOpen, setOverlayPanelIsOpen] = useState(false);
  const [medicationSelectorIsOpen, setMedicationSelectorIsOpen] = useState(false);
  const [selectedMedicationName, setSelectedMedicationName] = useState<string | undefined>();
  const selectedMedicationRef = useRef<Medication | undefined>(undefined);

  const isOptionElement =
    variant === "upsell" || variant === "comfort-care" || variant === "region-based";
  const showMedicationSelector =
    isOptionElement || variant === "consultation" || variant === "dashboard";

  // Only gate lab-exam medications in addon flows — respect prior selections
  const shouldGateLabExam = variant === "upsell" || variant === "region-based";
  const departureDate = useSelector<RootState, string | undefined>((state) =>
    shouldGateLabExam ? state.case.newCase.data.trip?.from : undefined
  );
  const caseTrip = useSelector<RootState, CaseUpdateInput["trip"]>(
    (state) => state.case.newCase.data.trip
  );

  const { displayWarningMessage } = useQuestionValidation();

  const { medications: allMedications, isLoading: isMedicationsLoading } = useFetchMedications(
    showMedicationSelector ? condition.product.name : ""
  );
  const medications = useAvailableMedications(allMedications, departureDate);

  const { filterPromotedMedications, computeShowSeeOptions } = useUpsellConfig();

  const promotedMedications = filterPromotedMedications(condition.product.name, medications);

  // Raw check for useEffects — preserves original behavior regardless of flag.
  const hasMultipleMedications = medications.length > 1;

  const showSeeOptions = computeShowSeeOptions(
    condition.product.name,
    medications,
    isCartPreselected,
    condition.suggestedMedication?.name,
    condition.suggestedMedication?.id
  );

  useEffect(() => {
    const defaultMedication = medications[0];
    if (!hasMultipleMedications && !selectedMedicationName && defaultMedication?.name) {
      setSelectedMedicationName(defaultMedication.name);
      dispatch(
        updateConditionMedication({
          conditionName: condition.product.name,
          medication: defaultMedication,
        })
      );
    }
  }, [
    medications,
    hasMultipleMedications,
    selectedMedicationName,
    condition.product.name,
    dispatch,
  ]);

  useEffect(() => {
    if (selectedMedicationRef.current || isMedicationsLoading) return;
    const resolved = resolveDisplayedMedicationName({
      medications,
      suggestedMedicationName: condition.suggestedMedication?.name,
      isCartPreselected,
      hasMultipleMedications,
    });
    if (resolved) {
      setSelectedMedicationName(resolved);
      if (isCartPreselected) {
        const preselectedMedication = allMedications.find(
          (med) => med.name === condition.suggestedMedication?.name
        );
        if (preselectedMedication) {
          selectedMedicationRef.current = preselectedMedication;
        }
      }
    }
  }, [
    condition.suggestedMedication,
    hasMultipleMedications,
    isMedicationsLoading,
    isCartPreselected,
    medications,
    allMedications,
  ]);

  const conditionPrice = useMemo(() => {
    const basePriceCents = condition?.price ?? 0;
    if (condition.product.name !== ConditionTypes.MALARIA_CONSULTATION || !selectedMedicationName) {
      return basePriceCents / 100;
    }
    const travelDays = caseTrip?.days ?? 0;
    const baseQuantity = resolveMedicationQuantity(
      medications,
      selectedMedicationName,
      condition.suggestedMedication?.quantity
    );
    const adjusted = calculateMalariaPricing(
      selectedMedicationName,
      travelDays,
      basePriceCents,
      baseQuantity
    );
    return adjusted.price / 100;
  }, [
    condition?.price,
    condition.product.name,
    condition.suggestedMedication?.quantity,
    selectedMedicationName,
    caseTrip?.days,
    medications,
  ]);

  const pricingWarning = useMemo((): NoticeMessage | null => {
    if (!warningMessage || condition.product.name !== ConditionTypes.MALARIA_CONSULTATION) {
      return null;
    }
    const answer = { trip: caseTrip } as CaseUpdateInput;
    const activeMedication =
      medications.find((med) => med.name === selectedMedicationName) ??
      condition.suggestedMedication;
    if (!activeMedication || !activeMedication.name) return null;
    const syntheticCondition = { ...condition, suggestedMedication: activeMedication };
    return displayWarningMessage(answer, warningMessage, [syntheticCondition]);
  }, [warningMessage, selectedMedicationName, caseTrip, condition, medications]);

  const toggleAddonSelection = () => {
    const isAdded = addonConditions
      .map((addonCondition) => addonCondition?.product.id)
      .includes(condition.product.id);

    if (isAdded) {
      removeCondition && removeCondition(condition.product.name);
    } else if ((showSeeOptions || isMedicationsLoading) && !selectedMedicationRef.current) {
      setMedicationSelectorIsOpen(true);
    } else {
      const medication = selectedMedicationRef.current ?? promotedMedications[0] ?? medications[0];
      if (medication && !selectedMedicationRef.current) {
        selectedMedicationRef.current = medication;
        setSelectedMedicationName(medication.name);
        dispatch(
          updateConditionMedication({
            conditionName: condition.product.name,
            medication,
          })
        );
      }
      addCondition?.(condition.product.name, medication);
    }
  };

  // The option card and its radio input share this handler (the card through onClick, the
  // input through onChange), so one activation of the input reaches it twice, and a click on
  // the label text makes the browser click the input right after the label. A patient's single
  // gesture must toggle the add-on once.
  const lastActivationRef = useRef<Event | null>(null);
  const activateAddonOption = (event: React.SyntheticEvent) => {
    if (lastActivationRef.current === event.nativeEvent) return;
    lastActivationRef.current = event.nativeEvent;

    // Cancel the label's follow-up click on the input: it would toggle the add-on back.
    if (!(event.target instanceof HTMLInputElement)) event.preventDefault();

    toggleAddonSelection();
  };

  return (
    <>
      {cartItemPresentationStyle === "selector" && isOptionElement ? (
        <ConditionOption
          data-testid={`questionnaire-radio-button-${condition.product.id}`}
          checked={addonConditions
            .map((addedCondition) => addedCondition.product.id)
            .includes(condition.product.id)}
          onClick={activateAddonOption}
        >
          <ConditionBody
            condition={condition}
            discount={discount}
            conditionPrice={conditionPrice}
            setOverlayPanelIsOpen={setOverlayPanelIsOpen}
            setMedicationSelectorIsOpen={setMedicationSelectorIsOpen}
            hasMultipleMedications={showSeeOptions}
            selectedMedication={selectedMedicationRef.current}
            selectedMedicationName={selectedMedicationName}
            removeCondition={removeCondition}
            addCondition={addCondition}
            cartItemPresentationStyle={cartItemPresentationStyle}
            variant={variant}
          />
        </ConditionOption>
      ) : (
        <ConditionCard>
          <ConditionBody
            condition={condition}
            discount={discount}
            conditionPrice={conditionPrice}
            setOverlayPanelIsOpen={setOverlayPanelIsOpen}
            setMedicationSelectorIsOpen={setMedicationSelectorIsOpen}
            hasMultipleMedications={showSeeOptions}
            selectedMedication={selectedMedicationRef.current}
            selectedMedicationName={selectedMedicationName}
            removeCondition={removeCondition}
            addCondition={addCondition}
            cartItemPresentationStyle={cartItemPresentationStyle}
            variant={variant}
          />
        </ConditionCard>
      )}
      <OverlayPanel
        open={overlayPanelIsOpen}
        close={() => setOverlayPanelIsOpen(false)}
        child={() => (
          <ConditionInfoContent
            conditionType={
              conditionTypeIndex.get(condition.product.name as ConditionTypes) ?? "plus"
            }
            conditionName={condition.product.name}
            pricingWarning={pricingWarning}
            selectedMedicationName={selectedMedicationName ?? condition.suggestedMedication?.name}
          />
        )}
      />
      {showMedicationSelector && (
        <OverlayPanel
          open={medicationSelectorIsOpen}
          close={() => {
            setMedicationSelectorIsOpen(false);
          }}
          child={() => (
            <MedicationSelector
              condition={condition}
              conditionType={conditionTypeIndex.get(condition.product.name as ConditionTypes) ?? "plus"}
              medications={promotedMedications}
              onClose={() => {
                setMedicationSelectorIsOpen(false);
              }}
              onConfirm={(medication: Medication) => {
                selectedMedicationRef.current = medication;
                setSelectedMedicationName(medication.name);
                dispatch(
                  updateConditionMedication({
                    conditionName: condition.product.name,
                    medication,
                  })
                );
                const isAlreadyAdded = addonConditions
                  .map((c) => c.product.id)
                  .includes(condition.product.id);
                if (!isAlreadyAdded) {
                  addCondition?.(condition.product.name, medication);
                }
              }}
            />
          )}
        />
      )}
    </>
  );
};

export default Condition;
